import { VehiclesImportResponse } from '../../models/vehicles-import-response';
import { EMPTY_ROWS_DESCRIPTOR } from '../../models/empty-rows-descriptor';
import {
  createStore,
  emitOnce,
  filterNil,
  select,
  withProps,
} from '@ngneat/elf';
import {
  getAllEntities,
  setEntities,
  withEntities,
} from '@ngneat/elf-entities';
import {
  clearRequestsStatus,
  createRequestsStatusOperator,
  selectRequestStatus,
  updateRequestStatus,
  withRequestsStatus,
} from '@ngneat/elf-requests';
import {
  FleetVehicleInformation,
  OrphanedResource,
  Vehicle,
  VehicleDriverLink,
  VehicleGroup,
} from '@tmx/data-access';
import { RowsDescriptor, SortDirection } from '@tmx/operandi';
import { shareReplay, Subject } from 'rxjs';

interface vehiclesProps {
  totalVehicles: number;
  hasVehicles: boolean;
  offset: number;
  sortExpression: string;
  sortDirection: '' | 'asc' | 'desc';
  query: string;
  vehicleRowsDescriptor: RowsDescriptor;
  selectedVehicle: Vehicle | null;
  selectedVehicleFleetInformation: FleetVehicleInformation | null;
  selectedVehiclesGroups: VehicleGroup[];
  selectedVehiclesGroupIds: number[];
  vehicleErrorMessage: string | null;
  creatingNewVehicle: boolean;
  prefilledVehicleId: string | null;
  orphans: Map<string, OrphanedResource> | null;
  importResponse: VehiclesImportResponse;
  linkedData: Map<string, VehicleDriverLink> | null;
}

const vehiclesStore = createStore(
  { name: 'vehicles' },
  withProps<vehiclesProps>({
    totalVehicles: 0,
    hasVehicles: false,
    offset: 0,
    query: '',
    sortExpression: '',
    sortDirection: SortDirection.None,
    vehicleRowsDescriptor: EMPTY_ROWS_DESCRIPTOR,
    selectedVehicle: null,
    selectedVehicleFleetInformation: null,
    selectedVehiclesGroups: [],
    selectedVehiclesGroupIds: [],
    vehicleErrorMessage: null,
    creatingNewVehicle: false,
    prefilledVehicleId: null,
    orphans: null,
    importResponse: new VehiclesImportResponse(),
    linkedData: null,
  }),
  withEntities<Vehicle>(),
  withRequestsStatus<
    | 'delete-vehicle'
    | 'edit-vehicles-groups'
    | 'linked-data'
    | 'vehicle-details'
    | 'vehicle-import'
    | 'vehicle-update'
    | 'vehicles-groups-ids'
    | 'vehicles'
  >()
);

export const trackVehiclesRequestsStatus =
  createRequestsStatusOperator(vehiclesStore);

export const vehicleErrorMessage$ = vehiclesStore.pipe(
  select((state) => state.vehicleErrorMessage),
  filterNil()
);

export const vehiclesStatus$ = vehiclesStore.pipe(
  selectRequestStatus('vehicles')
);

export const deleteVehicleStatus$ = vehiclesStore.pipe(
  selectRequestStatus('delete-vehicle')
);

export const hasVehicles$ = vehiclesStore.pipe(
  select((state) => state.hasVehicles)
);

export const vehicleRowsDescriptor$ = vehiclesStore.pipe(
  select((state) => state.vehicleRowsDescriptor)
);

export const orphans$ = vehiclesStore.pipe(
  select((state) => state.orphans),
  filterNil()
);

export const tableReload$ = new Subject<void>();
export const currentStepReset$ = new Subject<number>();
export const currentStepSave$ = new Subject<number>();

export const importResponse$ = vehiclesStore.pipe(
  select((state) => state.importResponse)
);

export function updateHasVehicles(hasVehicles: boolean) {
  vehiclesStore.update((state) => ({
    ...state,
    hasVehicles,
  }));
}

export function updateSelectedVehicle(
  selectedVehicle: Vehicle | null,
  selectedVehicleFleetInformation?: FleetVehicleInformation | null
) {
  if (selectedVehicleFleetInformation) {
    vehiclesStore.update((state) => ({
      ...state,
      selectedVehicleFleetInformation,
    }));
  }

  vehiclesStore.update(
    (state) => ({
      ...state,
      selectedVehicle,
    }),
    updateRequestStatus('vehicle-details', 'success')
  );
}

export const vehicleDetailsStatus$ = vehiclesStore.pipe(
  selectRequestStatus('vehicle-details'),
  shareReplay({ bufferSize: 1, refCount: true })
);

export const vehicleVehicleGroupsIdsStatus$ = vehiclesStore.pipe(
  selectRequestStatus('vehicles-groups-ids')
);

export const vehicleUpdateStatus$ = vehiclesStore.pipe(
  selectRequestStatus('vehicle-update')
);

export const importVehicleStatus$ = vehiclesStore.pipe(
  selectRequestStatus('vehicle-import')
);

export const linkedDataStatus$ = vehiclesStore.pipe(
  selectRequestStatus('linked-data')
);

export function updateVehicles(vehicles: Vehicle[], totalVehicles: number) {
  vehiclesStore.update(
    (state) => ({
      ...state,
      totalVehicles,
    }),
    setEntities(vehicles),
    updateRequestStatus('vehicles', 'success')
  );
}

export function updateOffset(offset: number) {
  vehiclesStore.update((state) => ({
    ...state,
    offset,
  }));
}

export function updateSort(
  sortExpression: string,
  sortDirection: '' | 'asc' | 'desc'
) {
  vehiclesStore.update((state) => ({
    ...state,
    sortExpression,
    sortDirection,
  }));
}

export function updateQuery(query: string) {
  vehiclesStore.update((state) => ({
    ...state,
    query,
  }));
}

export function updateVehicleRowsDescriptor(
  vehicleRowsDescriptor: RowsDescriptor
) {
  vehiclesStore.update((state) => ({
    ...state,
    vehicleRowsDescriptor,
  }));
}

export function updateSelectedVehiclesGroups(
  selectedVehiclesGroups: VehicleGroup[]
) {
  vehiclesStore.update((state) => ({
    ...state,
    selectedVehiclesGroups,
  }));
}

export function updateSelectedVehiclesGroupIds(
  selectedVehiclesGroupIds: number[]
) {
  vehiclesStore.update(
    (state) => ({
      ...state,
      selectedVehiclesGroupIds,
    }),
    updateRequestStatus('vehicles-groups-ids', 'success')
  );
}

export function updateVehiclesGroupsUpdateStatus() {
  vehiclesStore.update(updateRequestStatus('edit-vehicles-groups', 'success'));
}

export const vehiclesGroupsUpdateStatus$ = vehiclesStore.pipe(
  selectRequestStatus('edit-vehicles-groups')
);

export function updateCreatingNewVehicle(creatingNewVehicle: boolean) {
  vehiclesStore.update((state) => ({
    ...state,
    creatingNewVehicle,
  }));
}

export function updatePrefilledVehicleId(prefilledVehicleId: string | null) {
  vehiclesStore.update((state) => ({
    ...state,
    prefilledVehicleId,
  }));
}

export function updateOrphans(orphans: Map<string, OrphanedResource> | null) {
  vehiclesStore.update((state) => ({
    ...state,
    orphans,
  }));
}

export function updateLinkedData(
  linkedData: Map<string, VehicleDriverLink> | null
) {
  vehiclesStore.update(
    (state) => ({
      ...state,
      linkedData,
    }),
    updateRequestStatus('linked-data', 'success')
  );
}

export function resetVehiclesStatus() {
  vehiclesStore.update(updateRequestStatus('vehicles', 'idle'));
}

export function resetVehicleImportStatus() {
  vehiclesStore.update(updateRequestStatus('vehicle-import', 'idle'));
}

export function updateVehicleUpdateStatus() {
  vehiclesStore.update(updateRequestStatus('vehicle-update', 'success'));
}

export function updateDeleteVehicleStatus() {
  vehiclesStore.update(updateRequestStatus('delete-vehicle', 'success'));
}

export function updateVehicleImportStatus() {
  vehiclesStore.update(updateRequestStatus('vehicle-import', 'success'));
}

export function updateImportResponse(importResponse: VehiclesImportResponse) {
  vehiclesStore.update((state) => ({
    ...state,
    importResponse,
  }));
}

export function updateVehicleErrorMessage(vehicleErrorMessage: string | null) {
  vehiclesStore.update((state) => ({
    ...state,
    vehicleErrorMessage,
  }));
}

export function updateTableReload() {
  tableReload$.next();
}

export function updateCurrentStepReset(currentStep: number) {
  currentStepReset$.next(currentStep);
}

export function updateCurrentStepSave(currentStep: number) {
  currentStepSave$.next(currentStep);
}

export function clearSelectedVehicle() {
  vehiclesStore.update((state) => ({
    ...state,
    selectedVehicle: null,
    selectedVehicleFleetInformation: null,
  }));
}

export const vehicleStoreSnapshot = () => vehiclesStore.getValue();

export const vehicleEntitiesSnapshot = () =>
  vehiclesStore.query(getAllEntities());

export function clearVehicleStore() {
  emitOnce(() => {
    vehiclesStore.update(clearRequestsStatus());
    vehiclesStore.reset();
  });
}
